import React from 'react';
import { Link } from 'react-router-dom';
import { Store, Tag, Zap, Star, Wallet, Bot, Sparkles, Coins, Wrench, Gift, GitCompare, Users, Award, BookOpen, ArrowUpRight } from 'lucide-react';
import ChatLanding from '../components/ChatLanding';
import ThreeSteps from '../components/ThreeSteps';
import BuildPicker from '../components/BuildPicker';
import MarketplaceHow from '../components/MarketplaceHow';
import StartBuilding from '../components/StartBuilding';
import PhotoStatement from '../components/PhotoStatement';
import PapersTeaser from '../components/PapersTeaser';
import HarnessFlow from '../components/HarnessFlow';
import { useSeo } from '../hooks/useSeo';

// The Loreto Skills Marketplace landing page (the homepage). Leads with the Neo
// video hero, then sells the marketplace value proposition.
const VALUE = [
  {
    Icon: Zap,
    title: 'Skip the blank page.',
    body: 'Don’t build every skill from scratch. Grab a production-ready package — SKILL.md, README, references, and a runnable test — and drop it straight into .claude/skills/.',
  },
  {
    Icon: Star,
    title: 'Vetted by real usage.',
    body: 'Every skill carries star ratings, reviews, and download counts, so you can see what actually works in production before you install it.',
  },
  {
    Icon: Wallet,
    title: 'Earn from what you build.',
    body: 'List your own skills in a click. Set any price, reach the whole community, and keep 80% of every sale — we handle checkout and payouts.',
  },
];

const AGENTS = [
  {
    Icon: Bot,
    title: 'Build an agent avatar.',
    body: 'Spin up a named agent persona with its own profile, auto-generated avatar, and storefront — your own branded expert in the marketplace.',
  },
  {
    Icon: Sparkles,
    title: 'It generates specialized skills.',
    body: 'Point your agent at a video, article, PDF, or your own expertise and it scaffolds production-ready skill packages — SKILL.md, README, references, and a runnable test.',
  },
  {
    Icon: Coins,
    title: 'You earn when they sell.',
    body: 'List the skills your agent avatars create and keep the revenue — payouts settle to your USDC wallet or Stripe. Your agents, your storefront, your money.',
  },
];

// Contextual entry points into the static SEO layer. Native <a> (full navigation)
// because these are server-rendered static pages, not SPA routes.
const EXPLORE = [
  { Icon: Wrench, href: '/tools', title: 'Free tools for Claude Code', body: 'A SKILL.md linter, a Claude token & cost calculator, and an llms.txt generator — no signup.' },
  { Icon: Gift, href: '/free-skills', title: 'Free Claude Code skills', body: '14 production-ready skills across SEO, RAG and AI-ops you can download and run today.' },
  { Icon: GitCompare, href: '/compare', title: 'Compare skills & tools', body: 'Claude Code skills vs MCP, Loreto vs building by hand, RAG vs knowledge graph, and more.' },
  { Icon: Users, href: '/for', title: 'Skills by use case', body: 'Curated for RAG engineers, AI platform teams, SRE, SEO teams, and solo developers.' },
  { Icon: Award, href: '/best', title: 'Best Claude Code skills', body: 'Our round-ups — the strongest skills for RAG, SEO, AI evaluation, and the best free ones.' },
  { Icon: BookOpen, href: '/blog', title: 'Guides & deep dives', body: 'Answers to the hard questions about RAG, agents, harnesses, and context engineering.' },
];

/**
 * A file tree drawn as real text, big enough to read.
 *
 * `depth` indents, `accent` marks the thing the card is actually about. The
 * connectors are literal box-drawing characters rather than borders, because
 * this has to survive being screenshotted and pasted into a deck, which is
 * what people do with it.
 */
function Tree({ lines }) {
  // min-height so the two cards' headings sit on the same line: one tree is two
  // rows and the other is four, and without it the pair reads as a layout
  // accident. Only from md up: stacked on a phone there is nothing to align to
  // and the reserved height is a hole under a two line tree.
  return (
    <div className="rounded-sm border border-[var(--border-2)] bg-[var(--bg-2)] px-3 sm:px-5 py-4 overflow-x-auto md:min-h-[142px]">
      <pre className="mono text-[0.68rem] sm:text-[0.84rem] leading-[2] m-0 text-[var(--text-3)]">
        {lines.map(([label, depth, accent], i) => (
          <div key={label} className="whitespace-pre">
            {depth > 0 && (
              <span className="text-[var(--text-4)]">
                {'    '.repeat(depth - 1)}
                {i === lines.length - 1 || (lines[i + 1] && lines[i + 1][1] < depth) ? '└── ' : '├── '}
              </span>
            )}
            <span className={accent ? 'text-[var(--accent)]' : depth === 0 ? 'text-[var(--text-2)]' : ''}>
              {label}
            </span>
          </div>
        ))}
      </pre>
    </div>
  );
}

export default function Home() {
  useSeo({
    title: 'Loreto — Build your own harness',
    description:
      'The model is fixed; the harness around it is yours. Loreto reads your repository and writes the harness from its own practice — agent files, skills, references. Or start from a video, an article or a PDF and build one piece at a time.',
  });
  return (
    <>
      {/* (01) The harness itself, photographed. It opens the page: the one
          line under it is the whole argument the brand is built on, and it
          lands harder as an assertion than as a breath halfway down. `flush`
          because it sits directly under the nav and must not draw a rule
          against the one the nav already draws. */}
      <PhotoStatement
        num="01"
        eyebrow="The harness"
        flush
        image="/campaign/harness-wide.jpg"
        focus="70% center"
        side="right"
        insetClass="md:left-[8%]"
        shortFade
        overlay="linear-gradient(90deg, var(--bg) 0%, var(--bg) 12%, rgba(10,10,10,0.93) 24%, rgba(10,10,10,0.62) 36%, rgba(10,10,10,0.34) 50%, rgba(10,10,10,0.26) 100%)"
        headline={
          <>
            Build your own harness{' '}
            <span className="italic text-[var(--text-2)]">with Loreto.</span>
          </>
        }
      >
        <p>
          A harness is the part you own. It is what turns a general animal into
          one that works your ground, and nobody buys it off the shelf — it is
          fitted, to the work and to the team doing it.
        </p>
        <p>
          Your agent runs the same model as everyone else&rsquo;s. What it reads
          before it starts is the only thing that is yours.
        </p>
      </PhotoStatement>

      {/* (02) Git Timeline is the half of the product that writes a harness
          from a repository's own practice: the film still, then the two
          artifacts it produces. The composer below is the quick way in; this is
          the deep one. */}
      <section className="relative border-t border-[var(--border)] bg-[var(--bg)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-32">
          <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
            <span className="divider-num">(02)</span>
            <span className="eyebrow">Git Timeline — the harness generator</span>
            <span className="flex-1 thin-line ml-2" />
          </div>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] gap-10 lg:gap-14 items-center">
            <div>
              <h1 className="serif text-[2.8rem] md:text-[4.4rem] leading-[1.02] tracking-[-0.025em] max-w-[17ch]">
                The model is fixed.{' '}
                <span className="italic text-[var(--text-2)]">The harness is yours.</span>
              </h1>
              <p className="mt-6 text-[var(--text-2)] text-[1.06rem] leading-[1.65] max-w-[46ch]">
                Paste a GitHub URL. The whole history opens on one canvas — every branch, every
                release, every revert — and an investigator answers questions from it.
              </p>
              <p className="mt-5 text-[var(--text-2)] text-[1.06rem] leading-[1.65] max-w-[46ch]">
                Nobody wrote your conventions down. Your repository did. Loreto reads them and
                writes them into the files your agent already opens.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                {/* Lands on the Explorer with the demo already drawn and the
                    paste field focused. Sending it to /git-timeline put a
                    reader on a page that explains the product; they clicked a
                    button that says "build", so give them the box. */}
                <Link to="/git-timeline/mrdoob/three.js/?start=1" className="btn-primary !py-2.5 !px-5 !text-[0.88rem]">
                  Build your harness <ArrowUpRight size={15} />
                </Link>
                <Link to="/git-timeline/mrdoob/three.js/" className="btn-ghost !py-2.5 !px-5 !text-[0.88rem]">
                  Open the Explorer <ArrowUpRight size={15} />
                </Link>
                <span className="mono text-[0.72rem] text-[var(--text-4)]">three.js is open to everyone · your own repositories are private to you</span>
              </div>
            </div>

            {/* The loop is the demo: a real repository's history, then the
                harness written out of it. Muted and inline so it autoplays on
                iOS; the poster carries the first paint so nothing is blank
                while it buffers. */}
            <Link to="/git-timeline/mrdoob/three.js/" className="group block border border-[var(--border)] rounded-sm overflow-hidden bg-black hover:border-[var(--accent)] transition-colors">
              <video
                src="/demo/harness-demo.mp4"
                poster="/demo/harness-demo-poster.jpg"
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label="A year of mrdoob/three.js on the Git Timeline, then the agent file and skill packages Loreto writes from that history"
                className="w-full h-auto block"
              />
              <div className="px-5 py-3 border-t border-[var(--border)] flex items-center justify-between gap-4">
                <span className="mono text-[0.7rem] tracking-[0.14em] uppercase text-[var(--text-3)]">mrdoob/three.js · history in, harness out</span>
                <span className="mono text-[0.7rem] text-[var(--accent)] opacity-0 group-hover:opacity-100 transition-opacity">open →</span>
              </div>
            </Link>
          </div>

          {/* The whole product in one drawing, before any of the detail below
              it. A reader who takes nothing else from this page should still
              leave knowing a repository goes in and a harness comes out. */}
          <HarnessFlow />

          {/* What lands on disk, at a glance.
              This was two full screenshots of real output, each under a
              paragraph. Everything in them was true and almost none of it was
              legible: a reader had to decode a picture of a file before they
              learned what the file was for. The shape is the point, so the
              shape is what this draws — real text, four lines each, readable
              at a glance and on a phone. The full example is still one click
              away for anyone who wants to check the detail. */}
          <div className="mt-16 md:mt-20">
            <div className="flex items-baseline gap-3 mb-8">
              <span className="mono text-[0.7rem] tracking-[0.18em] uppercase text-[var(--text-3)]">What lands in your repo</span>
              <span className="flex-1 thin-line" />
            </div>

            {/* min-w-0 on the cells is load bearing. A grid item defaults to
                min-width:auto, so each track grew to the intrinsic width of the
                widest whitespace-pre line in its tree, took the card past the
                viewport on a phone, and clipped the wrapping paragraphs with
                it. The overflow-x-auto on the tree cannot help until the track
                is allowed to shrink. */}
            <div className="grid md:grid-cols-2 gap-px bg-[var(--border)] border border-[var(--border)] rounded-sm overflow-hidden">
              <div className="bg-[var(--bg)] min-w-0 p-5 sm:p-7 md:p-9">
                <Tree
                  lines={[
                    ['your-repo/', 0, false],
                    ['AGENTS.md', 1, true],
                  ]}
                />
                <h3 className="serif text-[1.4rem] sm:text-[1.7rem] leading-tight mt-6 sm:mt-7 mb-2">One file your agent reads first</h3>
                <p className="text-[var(--text-2)] text-[0.95rem] leading-[1.65]">
                  How you name a commit, what runs before a pull request, what gets reverted, who
                  owns which directory. Counted from your own history, not guessed.
                </p>
              </div>

              <div className="bg-[var(--bg)] min-w-0 p-5 sm:p-7 md:p-9">
                <Tree
                  lines={[
                    ['.claude/skills/', 0, false],
                    ['conducting-code-reviews/', 1, true],
                    ['SKILL.md · references/ · tests/', 2, false],
                    ['3 more packages', 1, false],
                  ]}
                />
                <h3 className="serif text-[1.4rem] sm:text-[1.7rem] leading-tight mt-6 sm:mt-7 mb-2">One folder per procedure</h3>
                <p className="text-[var(--text-2)] text-[0.95rem] leading-[1.65]">
                  The instructions, the references they lean on, and a test that runs — so a skill
                  can be reviewed like any other code.
                </p>
              </div>
            </div>


            <Link
              to="/git-timeline"
              className="mono text-[0.72rem] tracking-[0.14em] uppercase text-[var(--accent)]
                         inline-flex items-center gap-2 mt-8 hover:gap-3 transition-all"
            >
              See a real one, written from three.js <ArrowUpRight size={13} />
            </Link>
          </div>
        </div>
      </section>

      {/* The composer: the quick way in, one piece at a time. It used to open
          the page; the harness generator does now, so this is no longer a full
          viewport hero and carries a rule above it like any other section. */}
      <ChatLanding />


      {/* The whole journey in three steps, before any of the argument. A
          first-time reader should know what happens after they paste a link
          without scrolling past a photo essay to find out. */}
      <ThreeSteps />

      {/* Pick what to build — skill package or CLI + MCP */}
      <BuildPicker />

      {/* (02) The campaign argument, met before the reader is asked to buy into
          anything. Photo right. */}
      <PhotoStatement
        num="04"
        eyebrow="The idea"
        image="/campaign/track.jpg"
        focus="50% 66%"
        side="right"
        insetClass="md:left-[8%]"
        shortFade
        overlay="linear-gradient(90deg, var(--bg) 0%, var(--bg) 12%, rgba(10,10,10,0.93) 24%, rgba(10,10,10,0.62) 36%, rgba(10,10,10,0.34) 50%, rgba(10,10,10,0.26) 100%)"
        headline={
          <>
            Your agent isn’t bad at this.{' '}
            <span className="italic text-[var(--text-2)]">Nobody wrote it down.</span>
          </>
        }
      >
        <p>
          People spend years believing they lack some talent, when what they were
          missing was the written form of the thing — the count, the order, the
          setting nobody mentioned. Write it down once and the difficulty
          disappears, because it was never difficulty. It was a gap in the
          instructions.
        </p>
        <p>
          Agents fail the same way, for the same reason. A skill is what the
          expert knows, written down once, so it can be followed exactly.
        </p>
        <p>
          That is what customising a harness actually means. Not a better model
          — the same model, finally handed the part nobody had written down.
        </p>
      </PhotoStatement>

      {/* Value proposition */}
      <section className="relative border-t border-[var(--border)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-32">
          <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
            <span className="divider-num">(05)</span>
            <span className="eyebrow">Why the marketplace</span>
            <span className="flex-1 thin-line ml-2" />
          </div>
          <h2 className="serif text-[2.6rem] md:text-[4.2rem] leading-[1.04] tracking-[-0.02em] max-w-[22ch] mb-8">
            The fastest way to add a piece{' '}
            <span className="italic text-[var(--text-2)]">to your harness.</span>
          </h2>
          <p className="text-[var(--text-2)] text-[1.02rem] leading-[1.7] max-w-[64ch] mb-16">
            Everyone is running the same handful of models. The harness around the model — the
            instructions it reads before it starts, the tools it can reach, the files that tell it
            how your work is actually done — is the part you own, and the part that decides whether
            an agent is good at <em className="italic text-[var(--text)]">your</em> job or merely
            good in general. Your repository writes the parts it already knows about. The
            marketplace is where you get the rest without writing them yourself.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)] rounded-sm overflow-hidden">
            {VALUE.map(({ Icon, title, body }) => (
              <div key={title} className="bg-[var(--bg)] p-8 md:p-10 flex flex-col">
                <Icon size={26} className="text-[var(--accent)] mb-6" />
                <h3 className="serif text-[1.9rem] leading-tight mb-3">{title}</h3>
                <p className="text-[var(--text-2)] text-[0.95rem] leading-[1.65]">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How the marketplace works */}
      <MarketplaceHow />

      {/* Top of intent. The reader has had the argument, the price and the
          delivery mechanism; this is the first place below the hero where they
          can act on it. */}
      <StartBuilding />

      {/* Build an agent avatar */}
      <section className="relative border-t border-[var(--border)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-32">
          <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
            <span className="divider-num">(07)</span>
            <span className="eyebrow">Build an agent</span>
            <span className="flex-1 thin-line ml-2" />
          </div>
          <h2 className="serif text-[2.6rem] md:text-[4.2rem] leading-[1.04] tracking-[-0.02em] max-w-[24ch] mb-6">
            Give your agent avatar a storefront{' '}
            <span className="italic text-[var(--text-2)]">that earns.</span>
          </h2>
          <p className="text-[var(--text-2)] text-[1.02rem] leading-[1.7] max-w-[60ch] mb-16">
            Don’t just buy skills — build an AI agent avatar that creates them. Your agent
            generates specialized skills, lists them in the marketplace, and every sale pays out to you.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)] rounded-sm overflow-hidden">
            {AGENTS.map(({ Icon, title, body }) => (
              <div key={title} className="bg-[var(--bg)] p-8 md:p-10 flex flex-col">
                <Icon size={26} className="text-[var(--accent)] mb-6" />
                <h3 className="serif text-[1.9rem] leading-tight mb-3">{title}</h3>
                <p className="text-[var(--text-2)] text-[0.95rem] leading-[1.65]">{body}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-3">
            <Link to="/dashboard/agents" className="btn-primary !py-3 !px-6">
              <Bot size={16} /> Create an agent avatar
            </Link>
            <Link to="/dashboard/generate" className="btn-ghost !py-3 !px-6">
              <Sparkles size={16} /> Generate a skill
            </Link>
          </div>
        </div>
      </section>

      {/* Explore — contextual links into the free tools + SEO layer */}
      <section className="relative border-t border-[var(--border)] bg-[var(--bg-2)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-28 md:py-32">
          <div className="flex items-center gap-3 mb-12 text-[var(--text-3)]">
            <span className="divider-num">(08)</span>
            <span className="eyebrow">Explore</span>
            <span className="flex-1 thin-line ml-2" />
          </div>
          <h2 className="serif text-[2.6rem] md:text-[4.2rem] leading-[1.04] tracking-[-0.02em] max-w-[20ch] mb-16">
            Free to explore,{' '}
            <span className="italic text-[var(--text-2)]">before you build.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)] rounded-sm overflow-hidden">
            {EXPLORE.map(({ Icon, href, title, body }) => (
              <a
                key={href}
                href={href}
                className="group bg-[var(--bg)] p-8 md:p-10 flex flex-col hover:bg-[var(--surface)] transition-colors"
              >
                <Icon size={26} className="text-[var(--accent)] mb-6" />
                <h3 className="serif text-[1.9rem] leading-tight mb-3 group-hover:text-[var(--accent)] transition-colors">
                  {title}
                </h3>
                <p className="text-[var(--text-2)] text-[0.95rem] leading-[1.65]">{body}</p>
              </a>
            ))}
          </div>

          <PapersTeaser />
        </div>
      </section>

      {/* The payoff. Everyone is running the same animal; the harness is the
          difference. Placed before the last call so the argument lands as a
          picture rather than a claim. */}
      <PhotoStatement
        num="09"
        eyebrow="Why it matters"
        image="/campaign/horses.jpg"
        focus="50% 40%"
        side="right"
        overlay="linear-gradient(90deg, var(--bg) 0%, var(--bg) 8%, rgba(10,10,10,0.92) 20%, rgba(10,10,10,0.55) 34%, rgba(10,10,10,0.26) 50%, rgba(10,10,10,0.18) 100%)"
        headline={
          <>
            Everyone has the same horses.{' '}
            <span className="italic text-[var(--text-2)]">Not the same harness.</span>
          </>
        }
      >
        <p>
          The frontier models are a handful, and your competitor is running the
          same ones you are. Nobody wins that race on the animal.
        </p>
        <p>
          What is left is the fitting: what the agent reads first, which tools it
          can reach, and whose conventions it follows. That is the part you build,
          and it is the part Loreto builds from what you already have.
        </p>
      </PhotoStatement>

      {/* Final CTA — and the campaign's payoff.
          The page has spent (04) making the argument that failure is usually a
          missing written form rather than a missing talent. By the time a reader
          reaches the bottom they have already been handed the premise, so the
          question lands as a conclusion instead of a slogan. It is set in the
          site's own serif over the site's own rule: the campaign becomes the
          page rather than an advert placed on it. Photo mirrors (02) — left
          here, right up there — so the page has a rhythm instead of a motif
          repeated twice the same way. */}
      <section className="relative border-t border-[var(--border)] bg-[var(--bg)] overflow-hidden">
        <div
          className="photo-bleed photo-bleed--short absolute inset-0 md:left-[6%] bg-cover bg-no-repeat"
          style={{ backgroundImage: 'url(/campaign/lane.jpg)', backgroundPosition: '54% 54%' }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'linear-gradient(90deg, var(--bg) 0%, var(--bg) 11%, rgba(10,10,10,0.94) 26%, rgba(10,10,10,0.62) 38%, rgba(10,10,10,0.28) 52%, rgba(10,10,10,0.22) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-32 md:py-44">
          <div className="max-w-[46ch] md:max-w-[34ch]">
            <span className="eyebrow text-[var(--text-3)]">[ Join the marketplace ]</span>
            <h2 className="serif text-[4.4rem] md:text-[6.4rem] leading-[0.98] tracking-[-0.03em] mt-8 mb-7">
              got skills?
            </h2>
            <div className="w-[210px] h-[2px] bg-[var(--rust)] mb-8" />
            <p className="text-[var(--text-2)] text-[1.05rem] md:text-[1.15rem] leading-[1.7] mb-12">
              Browse a growing catalog of Claude Code skills, or list your own and start earning.
              Free to join — you only pay when you buy, and only earn when you sell.
            </p>
            <div className="flex flex-wrap items-center gap-3 md:w-max">
              <Link to="/marketplace" className="btn-primary !py-3.5 !px-7 !text-[1rem]">
                <Store size={18} /> Browse the marketplace
              </Link>
              <Link to="/dashboard/sell" className="btn-ghost !py-3.5 !px-7 !text-[1rem]">
                <Tag size={18} /> Sell a skill
              </Link>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}
